/**
 * CpcDownloadModal
 *
 * Thin orchestrator that skips the criteria panel and goes straight to
 * OpsRetrievalPreviewPanel for a fixed set of CPC/IPC class codes.
 * All preview/launch/KB state is managed by useOpsRetrievalJob.
 */

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import OpsRetrievalPreviewPanel from './OpsRetrievalPreviewPanel';
import OpsRetrievalFallbackDialog from './OpsRetrievalFallbackDialog';
import { useOpsRetrievalJob } from '../hooks/useOpsRetrievalJob';
import { type OpsAdvancedQuery } from '../services/opsRetrievalService';

interface CpcDownloadModalProps {
  subProjectId: string;
  selectedClasses: string[];
  onClose: () => void;
}

export default function CpcDownloadModal({ subProjectId, selectedClasses, onClose }: CpcDownloadModalProps) {
  const { t } = useTranslation();

  const query: OpsAdvancedQuery = { cpc: selectedClasses, country_codes: ['EP'] };

  const job = useOpsRetrievalJob(subProjectId);

  // Kick off preview immediately on mount
  useEffect(() => {
    job.startPreview(query);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-screen-2xl flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 flex-shrink-0">
          <div>
            <h2 className="text-base font-semibold text-slate-800">
              {t('cpcSearch.downloadModalTitle')}
            </h2>
            <p className="text-xs text-slate-500 font-mono">{selectedClasses.join('  ·  ')}</p>
          </div>
          <span
            className="text-slate-400 hover:text-slate-600 text-xl cursor-pointer leading-none"
            onClick={onClose}
          >
            &times;
          </span>
        </div>

        {/* Body */}
        {job.previewing && (
          <div className="flex-1 flex items-center justify-center gap-3 p-10 text-sm text-slate-500">
            <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            {t('cpcSearch.downloadCounting')}
          </div>
        )}

        {job.previewError && !job.previewing && (
          <div className="flex-1 flex flex-col items-center justify-center gap-3 p-10">
            <p className="text-sm text-red-600">{job.previewError}</p>
            <button
              className="px-3 py-2 text-sm bg-slate-100 text-slate-700 rounded hover:bg-slate-200"
              onClick={onClose}
            >
              {t('common.close', 'Close')}
            </button>
          </div>
        )}

        {!job.previewing && job.previewResult && (
          <OpsRetrievalPreviewPanel
            previewResult={job.previewResult}
            samplesLoading={job.samplesLoading}
            samplesError={job.samplesError}
            kbs={job.kbs}
            kbsLoading={job.kbsLoading}
            selectedKbId={job.selectedKbId}
            setSelectedKbId={job.setSelectedKbId}
            newKbName={job.newKbName}
            setNewKbName={job.setNewKbName}
            kbReady={job.kbReady}
            launching={job.launching || job.testingGooglePatents}
            launchError={job.launchError}
            onModify={onClose}
            onLaunch={(mode, limit) => job.handleLaunch(mode, query, limit)}
          />
        )}

        {job.testingGooglePatents && (
          <div className="absolute inset-0 bg-white/70 flex items-center justify-center rounded-lg z-10">
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
              {t('opsRetrieval.testingGooglePatents', 'Testing fast download...')}
            </div>
          </div>
        )}
      </div>

      {job.showFallbackDialog && job.previewResult && (
        <OpsRetrievalFallbackDialog
          estimatedFullSeconds={job.previewResult.estimated_full_seconds}
          onConfirm={() => job.handleFallbackConfirm(query)}
          onCancel={() => job.setShowFallbackDialog(false)}
        />
      )}
    </div>
  );
}
